// =============================================================================
// Data keys
// =============================================================================

/**
 * Symbol key used as the default x-axis data key when no XAxis component is provided.
 *
 * WHY A SYMBOL?
 * Using a symbol guarantees uniqueness - it can never collide with user-provided
 * string keys in the data (like 'timestamp', 'date', 'x', etc.).
 *
 * HOW IT WORKS:
 * When no XAxis is provided, ChartProvider transforms the data by adding this symbol
 * as a key with the array index as its value:
 *
 * Input:
 * ```ts
 * [{apiV1: 100}, {apiV1: 120}, {apiV1: 90}]
 * ```
 *
 * Transformed (internally):
 * ```ts
 * [
 *   {apiV1: 100, [INDEX_X_DATA_KEY]: 0},
 *   {apiV1: 120, [INDEX_X_DATA_KEY]: 1},
 *   {apiV1: 90, [INDEX_X_DATA_KEY]: 2}
 * ]
 * ```
 *
 * The chart can then position data points along the x-axis using these index values
 * (0, 1, 2...) without requiring an explicit x-axis field in the data.
 *
 * @see ChartProvider - where the data transformation occurs
 * @see XDataKey - the type that allows either this symbol or a user-provided string key
 */
export const INDEX_X_DATA_KEY: unique symbol = Symbol('chartIndex');

// =============================================================================
// Chart (layout, typography, defaults)
// =============================================================================

/**
 * Fallback chart margins. Top/right provide minimal padding so data points near the edge
 * are not clipped. Bottom/left default to 0 because useAutoMargin computes them from the
 * XAxis and YAxis components actually present in the chart.
 */
export const CHART_MARGIN_DEFAULT = {
  top: 8,
  right: 8,
  bottom: 0,
  left: 0,
};

/**
 * Default x-scale type for LineChart when XAxis doesn't specify an xScaleType.
 */
export const LINE_CHART_X_SCALE_TYPE_DEFAULT = 'time' as const;

/**
 * Default x-scale type for ColumnChart when XAxis doesn't specify an xScaleType.
 */
export const COLUMN_CHART_X_SCALE_TYPE_DEFAULT = 'band' as const;

/**
 * Fallback font size (px) for axis labels when the CSS variable is unavailable (e.g. SSR or tests).
 */
export const CHART_FONT_SIZE_FALLBACK_PX = 14;

/**
 * CSS variable name for chart font size. Used for axis labels and other small text in charts.
 */
export const CHART_FONT_SIZE_CSS_VAR = '--lp-font-size-100';

// =============================================================================
// Axis (ticks, labels, padding)
// =============================================================================

/** Tick mark length (px). Matches chartTheme tickLength. */
export const AXIS_TICK_LENGTH_PX = 4;

/** Gap (px) between the tick mark and the label text. */
export const AXIS_TICK_LABEL_GAP_PX = 8;

/** Approximate height (px) of a single axis tick label line (~14px font + line-height). */
export const AXIS_TICK_LABEL_HEIGHT_PX = 16;

/** Extra padding (px) between the label and the chart edge. */
export const AXIS_PADDING_PX = 8;

/**
 * Approximate pixel width per character for Y-axis tick labels.
 * Based on the chart's label font (medium weight, ~11–14px).
 */
export const AXIS_AVG_CHAR_WIDTH_PX = 6;

/**
 * Number of evenly-spaced values sampled from the scale domain when explicit tick values
 * are not available (e.g. ColumnChart). Used for auto-margin width estimation.
 */
export const AXIS_DOMAIN_SAMPLE_COUNT = 6;

/** Minimum pixels between ticks to avoid overlap. */
export const AXIS_MIN_TICK_SPACING_PX = 60;

/** Default label length for estimation when no values available (e.g. "Jan 17" ~ 7 chars). */
export const AXIS_LABEL_LENGTH_DEFAULT = 7;

/**
 * Approximate character width ratio for axis labels.
 * Average character width is roughly 0.6x the font size for proportional fonts.
 */
export const AXIS_CHAR_WIDTH_RATIO = 0.6;

/**
 * Maximum relative variance allowed for approximate even spacing (25%).
 * When spacing >= 1/0.25 = 4, approximate spacing is acceptable.
 */
export const AXIS_MAX_RELATIVE_VARIANCE = 0.25;

/**
 * Minimum ideal spacing required for approximate (non-exact) even spacing.
 * Derived from AXIS_MAX_RELATIVE_VARIANCE.
 */
export const AXIS_MIN_SPACING_FOR_APPROXIMATE = 1 / AXIS_MAX_RELATIVE_VARIANCE;

/** Minimum number of ticks to display (e.g. first and last). */
export const AXIS_MIN_TICK_COUNT = 2;

/**
 * Minimum height (px) reserved per Y-axis tick when estimating an appropriate tick count
 * from the chart container height. Lower values produce denser tick grids.
 */
export const AXIS_Y_MIN_TICK_HEIGHT_PX = 40;

/** Minimum padding (px) between adjacent tick labels. */
export const AXIS_MIN_LABEL_PADDING_PX = 8;

// =============================================================================
// Tooltip
// =============================================================================

/**
 * Distance threshold (px) for tooltip and reference line snapping.
 * Crosshair and tooltips appear when cursor is within this distance from a data point or reference line.
 */
export const TOOLTIP_SNAP_DISTANCE_PX = Number.MAX_SAFE_INTEGER;

/**
 * Grace period (ms) after the pointer leaves the SVG before a pinned tooltip is dismissed.
 */
export const TOOLTIP_DISMISS_DELAY_MS = 150;

/** Pointer must move fewer than this many pixels between down and up to count as a click (not a drag). */
export const TOOLTIP_MIN_CLICK_DISTANCE_PX = 5;

// =============================================================================
// Zoom (drag-to-zoom)
// =============================================================================

/** Distance (px) within which drag-to-zoom start/end positions snap to nearby data points. */
export const ZOOM_SNAP_DISTANCE_PX = 30;

/** Minimum drag distance (px) to distinguish an intentional zoom selection from an accidental click. */
export const ZOOM_MIN_DRAG_DISTANCE_PX = 5;

/**
 * Fraction of the domain range added as padding at each edge of continuous (time/linear) scales.
 */
export const ZOOM_DOMAIN_EDGE_PADDING = 0.01;

// =============================================================================
// Domain (scale math)
// =============================================================================

/**
 * Absolute value threshold above which a number is treated as "effectively infinite"
 * (e.g. API sentinels). Used to exclude outliers from domain calculation.
 */
export const DOMAIN_EFFECTIVELY_INFINITE_THRESHOLD = 1e300;

// =============================================================================
// Series (markers, event sources)
// =============================================================================

/** Radius (px) of the circle markers shown at stacked-area series boundaries on hover. */
export const SERIES_AREA_STACK_MARKER_RADIUS_PX = 4;

export const SERIES_EVENT_SOURCE_BAR = 'BARSERIES_EVENT_SOURCE';
export const SERIES_EVENT_SOURCE_XY = 'XYCHART_EVENT_SOURCE';

// =============================================================================
// Bar / Column chart rendering
// =============================================================================

/**
 * Color of the thin separator line drawn at the top of each stacked bar segment.
 * Uses `--dataviz-bar-gap-line-color` so consumers can override just this value
 * (e.g. MiniColumnChart sets it to transparent) without cascading into unrelated
 * SVG elements that also reference `--lp-color-bg-ui-primary` (e.g. label halos).
 */
export const BAR_STACKED_GAP_LINE_COLOR = 'var(--dataviz-bar-gap-line-color, var(--lp-color-bg-ui-primary))';

/** Width (px) of the stacked-bar gap separator line. */
export const BAR_STACKED_GAP_LINE_WIDTH_PX = 1.5;

/** Border radius (px) applied to the top corners of the topmost bar in a stack. */
export const BAR_STACKED_TOP_RADIUS_PX = 4;

/** Minimum rendered height (px) for bars with positive values. */
export const BAR_MIN_HEIGHT_PX = 3;

/** Show gap only when BAR segments are stacked and are at least this tall (px). */
export const BAR_STACKED_MIN_SEGMENT_HEIGHT_FOR_GAP = 3;

/**
 * Opacity multiplier applied to a series that is dimmed (de-emphasized) while
 * another series is spotlighted — e.g. when hovering a legend label. Applied as
 * a fraction of the series' normal opacity so dimming scales with the base value.
 */
export const DIM_OPACITY_FACTOR = 0.2;

/** Gap (px) between sibling bars in a grouped (side-by-side) layout. */
export const BAR_GROUPED_INTRA_GROUP_GAP_PX = 1;

// =============================================================================
// Reference line labels
// =============================================================================

/** Font size (px) for the value label on horizontal reference lines. */
export const HORIZONTAL_REF_LINE_LABEL_FONT_SIZE = 13;

/** Font weight for horizontal reference line labels (bold for legibility over grid lines). */
export const HORIZONTAL_REF_LINE_LABEL_FONT_WEIGHT = 700;

/** Stroke width (px) of the white "halo" behind horizontal label text (improves contrast). */
export const HORIZONTAL_REF_LINE_LABEL_STROKE_WIDTH = 3;

/** Horizontal offset (px) from the right edge of the chart area to the label text. */
export const HORIZONTAL_REF_LINE_LABEL_X_OFFSET = 12;

/** Vertical offset (px) nudging the label above the reference line. */
export const HORIZONTAL_REF_LINE_LABEL_Y_OFFSET = 4;

/** Extra pixels of clearance to prevent a label from being clipped by the chart boundary. */
export const REF_LINE_LABEL_SAFETY_BUFFER_PX = 3;

/** Vertical offset (px) nudging vertical reference line labels below the top of the chart. */
export const VERTICAL_REF_LINE_LABEL_Y_OFFSET = 4;

// =============================================================================
// Mini chart (sparkline) defaults
// =============================================================================

export const MINI_CHART_DEFAULT_WIDTH = 106;
export const MINI_CHART_DEFAULT_HEIGHT = 26;

/** Gap (px) between an outside-left label and the chart left boundary. */
export const MINI_CHART_LABEL_GAP = 4;

/** Font size (px) for labels rendered outside the mini chart area. */
export const MINI_CHART_LABEL_FONT_SIZE = 11;

/**
 * Approximates the cap-height ratio for vertically centering SVG text on a baseline.
 * Multiplying a font size by this factor gives the vertical offset needed to align
 * the visual center of the glyphs with a horizontal line at that y-position.
 */
export const SVG_TEXT_CAP_HEIGHT_RATIO = 0.35;

// =============================================================================
// Pointer events (sentinels)
// =============================================================================

export const POINTER_EVENTS_ALL = '__POINTER_EVENTS_ALL';
export const POINTER_EVENTS_NEAREST = '__POINTER_EVENTS_NEAREST';

// =============================================================================
// Colors
// =============================================================================

/**
 * Default color palette for chart series.
 * Uses hex values from the LaunchPad design system for consistent colors across all rendering contexts.
 *
 * @see packages/flags/src/getVariationColorPalette.ts for the source of the colors
 */
export const COLORS_PALETTE_DEFAULT = [
  '#34B6D0', // Cyan (from --lp-color-brand-cyan-base)
  '#EEC340', // Gold (from --lp-color-brand-gold-base)
  '#A34FDE', // Purple (from --lp-color-brand-purple-base)
  '#00B969', // Green (from --lp-color-brand-green-base)
  '#FF386B', // Pink (from --lp-color-brand-pink-base)
  '#282828', // Dark Gray (from --lp-color-gray-900)
  '#40E39C', // Mint
  '#CAA636', // Olive
  '#7A3BA7', // Deep Purple
  '#3DD6F5', // Sky Blue
  '#D9305B', // Rose
  '#BCBEC0', // Light Gray (from --lp-color-gray-400)
];
